import chroma from "chroma-js";
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Color } from "three";
import {
  setCoordinate,
  toggleSensorsVirtualBox,
  updateClickedSolarSensor,
  updateVirtualSensorInfo,
} from "../../redux/solarSensors";
import { useColorBar } from "../CustomHooks";
import { sunsensors } from "../navBar/NavBar";
import {
  filterObjectsByColorbarRange,
  getRangeModeDisplayColorFn,
} from "../utils";
import { SphereInstancedMesh } from "./InstancedMesh";

const calculateColor = (value, minValue, maxValue, colorBar) => {
  const normalizedValue = (value - minValue) / (maxValue - minValue);
  return chroma.scale(colorBar)(normalizedValue).css();
};

const SunHours = ({
  sensorRow,
  id_val,
  colorBar,
  sunval,
  name,
  minIdx,
  maxIdx,
}) => {
  const dispatch = useDispatch();
  const minMaxRangeList = useSelector((state) => state.sensorSolars.rangeList);
  const minValue = minMaxRangeList[minIdx];
  const maxValue = minMaxRangeList[maxIdx];

  const { max, min, rangeMode } = useColorBar(
    "Solar Sensors",
    sunsensors[id_val],
    minValue,
    maxValue,
  );

  const displaySensors = useMemo(
    () =>
      filterObjectsByColorbarRange(
        sensorRow,
        (sensor) => sensor[sunval],
        min,
        max,
        rangeMode,
      ),
    [sensorRow, sunval, min, max, rangeMode],
  );

  const setColor = useCallback(
    (sensor) => {
      const { color } = getRangeModeDisplayColorFn(sensor[sunval], {
        min,
        max,
        rangeMode,
        getScaledColor: (value) =>
          calculateColor(value, Number(min), Number(max), colorBar),
      });
      return new Color(color);
    },
    [sunval, min, max, rangeMode],
  );

  const onClick = useCallback(
    (config) => {
      const sensor = displaySensors[config.instanceId];
      if (!sensor) return;
      const {
        name,
        sunshine_hours,
        direct_energy,
        diffuse_energy,
        global_energy,
        utm_x,
        utm_y,
        utm_z,
      } = sensor;

      dispatch(toggleSensorsVirtualBox());
      dispatch(
        updateVirtualSensorInfo([
          name,
          sunshine_hours,
          direct_energy,
          diffuse_energy,
          global_energy,
          utm_x,
          utm_y,
          utm_z,
        ]),
      );
      dispatch(updateClickedSolarSensor(sensor));
      dispatch(setCoordinate([config.pageX, config.pageY]));
      config.stopPropagation();
    },
    [displaySensors, dispatch, sunval],
  );

  const clickedSensor = useSelector(
    (state) => state.sensorSolars.clickedSensor,
  );
  const showSensorInfoBox = useSelector(
    (state) => state.sensorSolars.showSensorInfoBox,
  );

  return (
    <SphereInstancedMesh
      objects={displaySensors}
      setColor={setColor}
      groupProps={{ onClick }}
      updateDependencyArray={[min, max, rangeMode, sunval]}
      highlightProps={{
        highlightedObject: [clickedSensor],
        showHighlight: showSensorInfoBox,
      }}
    />
  );
};

export default SunHours;
